import React, { useState } from 'react'

import { Button, Card } from '@mui/material'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { numberFormatter } from '@albacross/frontend-utils'

import { Flag } from 'components/_base'
import { CampaignTargetsMatchButton } from 'components/campaign'
import { Table } from 'components/table'
import { useAddNotification } from 'hooks/useAddNotification'
import { useImportTargets } from 'hooks/useCampaignTargets'
import { useMatchResults } from 'hooks/useMatch'
import { findNaceCode } from 'utils/nace'

const CampaignTargetsMatchSummary = () => {
  const { id } = useParams()
  // Immutable MatchReducerRecord typings resolve to a plain Map, so use `any` for the collection API (.filter/.slice/.keySeq).
  const matching: any = useMatchResults().data
  const selected = matching.matched
    .filter((x: any) => x.selected)
    .keySeq()
    .toList()
  const navigate = useNavigate()
  const addNotification = useAddNotification()
  const { mutate, isPending } = useImportTargets(id)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const handleSubmit = () =>
    mutate(selected, {
      onSuccess: () => {
        addNotification('Targets added successfully', 'success')
        navigate(UrlProvider.getUrl('campaign.tab', { id, tab: 'targets' }))
      },
      onError: () => addNotification('Error occurred when adding targets', 'error'),
    })

  if (matching.pristine) {
    return <Navigate replace to={UrlProvider.getUrl('campaign.tab', { id, tab: 'targets' })} />
  }

  const config = [
    { title: 'Company Name', field: 'name' },
    { title: 'Country', render: ({ country }: any) => <Flag country={country} /> },
    { title: 'Industry', render: ({ naceCode }: any) => findNaceCode(naceCode)?.name || '' },
    { title: 'Website', field: 'website' },
    {
      title: 'Employees',
      render: ({ employees }: any) => (employees ? `${employees.from} – ${employees.to}` : ''),
    },
    {
      title: 'Revenue',
      render: ({ revenue }: any) =>
        revenue ? `${numberFormatter(revenue.from)} – ${numberFormatter(revenue.to)}` : '',
    },
    {
      title: 'Actions',
      render: ({ id: rowId, selected: rowSelected }: any) => (
        <CampaignTargetsMatchButton id={rowId} selected={rowSelected} />
      ),
    },
  ]

  return (
    <Card>
      <Table
        config={config}
        count={matching.matched.size}
        data={matching.matched.slice(page * rowsPerPage, (page + 1) * rowsPerPage)}
        onChangePage={setPage}
        onChangeRowsPerPage={setRowsPerPage}
        page={page}
        rowsPerPage={rowsPerPage}
        title="Matched companies"
        toolbar={
          <Button loading={isPending} onClick={handleSubmit}>
            Add targets to campaign
          </Button>
        }
      />
    </Card>
  )
}

export default CampaignTargetsMatchSummary
